import { api } from '../api.ts';
import { passwortWechseln } from '../crypto/vault.ts';
import { ladeEigeneVertrauenspersonen } from '../vertrauensperson.ts';
import { clear, el, formatBytes, hinweis, karte, passwortfeld } from '../ui.ts';
import type { BerichtFreigabe } from '../bericht.ts';
import {
  fehlertext,
  kopfzeile,
  root,
  sorgeFuerEntschluesselte,
  sorgeFuerMetadaten,
  state,
  type LogRow,
} from './gemeinsam.ts';

/**
 * Ansicht „Mein Konto" (nur Kunden): Kontodaten, Passwort-Erklärung und die
 * Dateneinsicht – was auf unseren Servern über den Kunden liegt und, genauso
 * wichtig, was dort nicht lesbar ist.
 *
 * Die Dateneinsicht ist bewusst aus den Metadaten der laufenden Sitzung
 * gebaut: Sie zeigt exakt die Sicht, die auch der Server hat. Entschlüsselt
 * wird hier nichts.
 */

export async function renderKonto(): Promise<void> {
  clear(root);
  root.append(kopfzeile('konto'), el('p', { class: 'loading' }, 'Wird geladen …'));

  try {
    await sorgeFuerMetadaten();
  } catch (error) {
    clear(root);
    root.append(kopfzeile('konto'), hinweis(fehlertext(error), 'warnung'));
    return;
  }

  clear(root);
  const altbestand = state.me?.personId ? state.documents.filter((m) => !m.wrap_ziel_art || m.wrap_ziel_art === 'customer').length : 0;
  root.append(
    kopfzeile('konto'),
    kontoKarte(),
    ...(altbestand > 0 ? [altbestandKarte(altbestand)] : []),
    exportKarte(),
    berichteKarte(),
    dateneinsichtKarte(),
  );
}

// --- Altbestand übernehmen (früher in der Organisationsansicht) ---------------

/**
 * Dokumente aus der Zeit vor dem eigenen Safe hängen noch am alten Kontoschlüssel.
 * Lesbar sind sie; nur der Familien-Safe-Mechanismus (Umschläge je Safe) kennt sie
 * nicht. Ein Klick zieht sie in „Mein Safe" um.
 */
function altbestandKarte(gesamt: number): HTMLElement {
  const status = el('div', { id: 'altbestand-status' });
  return karte(
    el('h2', {}, 'Alte Dokumente übernehmen'),
    el(
      'p',
      {},
      `${gesamt} ${gesamt === 1 ? 'Dokument stammt' : 'Dokumente stammen'} noch aus der Zeit vor Ihrem Safe. ` +
        'Lesen können Sie sie weiterhin – mit einem Klick gehören sie auch technisch zu „Mein Safe".',
    ),
    status,
    el(
      'div',
      { class: 'knopfreihe' },
      el('button', { id: 'altbestand-uebernehmen', onclick: () => void altbestandUebernehmen(status) }, 'Alte Dokumente übernehmen'),
    ),
  );
}

async function altbestandUebernehmen(status: HTMLElement): Promise<void> {
  const knopf = root.querySelector<HTMLButtonElement>('#altbestand-uebernehmen');
  if (knopf) knopf.disabled = true;
  clear(status);
  status.append(el('p', { class: 'loading' }, 'Wird übernommen …'));
  try {
    const { alteDokumenteUebernehmen } = await import('../documents.ts');
    const { safeSchluessel, meinSafe } = await import('../safes.ts');
    const mein = meinSafe();
    const schluessel = mein ? safeSchluessel(mein.kundeId) : null;
    const ergebnisse = await alteDokumenteUebernehmen((erledigt, gesamt) => {
      clear(status);
      status.append(el('p', { class: 'loading' }, `${erledigt} von ${gesamt} übernommen …`));
    }, schluessel ? { publicKeyRaw: schluessel.publicKeyRaw, algorithm: schluessel.algorithm } : undefined);
    const fehl = ergebnisse.filter((e) => !e.ok);
    clear(status);
    status.append(hinweis(fehl.length ? `${ergebnisse.length - fehl.length} übernommen, ${fehl.length} fehlgeschlagen – erneut versuchen holt nur die fehlgeschlagenen nach.` : `Alle ${ergebnisse.length} Dokumente übernommen.`, fehl.length ? 'warnung' : 'erfolg'));
    const { datenVerwerfen } = await import('./gemeinsam.ts');
    datenVerwerfen();
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  } finally {
    if (knopf) knopf.disabled = false;
  }
}

// --- Voll-Export ------------------------------------------------------------

function exportKarte(): HTMLElement {
  const status = el('div', { id: 'export-status' });
  return karte(
    el('h2', {}, 'Voll-Export'),
    el(
      'p',
      {},
      'Alle Ihre Dokumente mit der Ordnerstruktur als ZIP-Dateien zum Sichern auf Ihrem Gerät. ' +
        'Bei vielen Dokumenten entstehen mehrere Teilpakete von je etwa 250 MB.',
    ),
    el(
      'p',
      { class: 'leise' },
      'Die Dokumente werden auf Ihrem Gerät entschlüsselt – die Dateien im Export sind danach unverschlüsselt. ' +
        'Bewahren Sie sie sicher auf. Erlauben Sie Ihrem Browser mehrere Downloads, falls er nachfragt.',
    ),
    status,
    el(
      'div',
      { class: 'knopfreihe' },
      el('button', { id: 'export-starten', onclick: () => void exportStarten(status) }, 'Alle Dokumente exportieren (ZIP)'),
    ),
  );
}

async function exportStarten(status: HTMLElement): Promise<void> {
  const knopf = root.querySelector<HTMLButtonElement>('#export-starten');
  if (knopf) knopf.disabled = true;
  const zeige = (text: string): void => {
    clear(status);
    status.append(el('p', { class: 'loading' }, text));
  };
  zeige('Dokumente werden entschlüsselt …');
  try {
    const { vollExport } = await import('./gemeinsam.ts');
    const r = await vollExport(zeige);
    clear(status);
    status.append(
      hinweis(
        `${r.dokumente} Dokumente in ${r.pakete} ZIP-Datei(en) exportiert – sie liegen in Ihren Downloads.` +
          (r.fehler.length ? ` Nicht exportiert (${r.fehler.length}): ${r.fehler.slice(0, 5).join('; ')}` : ''),
        r.fehler.length ? 'warnung' : 'erfolg',
      ),
    );
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  } finally {
    if (knopf) knopf.disabled = false;
  }
}

// --- Berichte ---------------------------------------------------------------

/**
 * Zugriffsbericht als PDF (Etappe 2): Freigabe-Historie und
 * Zugriffsprotokoll zum Abheften — etwa für eine Prüfung oder die eigene
 * Ablage. Erzeugt wird der Bericht vollständig im Browser; die
 * Dokumentnamen darin entschlüsselt das Gerät, der Server kennt sie nicht.
 */
function berichteKarte(): HTMLElement {
  const status = el('div', { id: 'bericht-status' });

  return karte(
    el('h2', {}, 'Berichte'),
    el(
      'p',
      {},
      'Der Zugriffsbericht fasst zusammen, wer wann auf Ihre Unterlagen ' +
        'zugegriffen hat und welche Freigaben es gab – zum Abheften, für die ' +
        'eigene Ablage oder eine Prüfung.',
    ),
    el(
      'p',
      { class: 'leise' },
      'Der Bericht wird auf Ihrem Gerät erzeugt und dabei nirgendwohin ' +
        'übertragen. Die Dokumentnamen darin sieht nur, wer die PDF-Datei hat.',
    ),
    status,
    el(
      'div',
      { class: 'knopfreihe' },
      el(
        'button',
        { id: 'bericht-erzeugen', onclick: () => void berichtErzeugen(status) },
        'Zugriffsbericht (PDF) erstellen',
      ),
    ),
  );
}

async function berichtErzeugen(status: HTMLElement): Promise<void> {
  const knopf = root.querySelector<HTMLButtonElement>('#bericht-erzeugen');
  if (knopf) knopf.disabled = true;
  clear(status);
  status.append(el('p', { class: 'loading' }, 'Unterlagen werden zusammengestellt …'));

  try {
    // Dokumentnamen gibt es nur entschlüsselt — das kann bei vielen
    // Dokumenten dauern, passiert aber nur auf ausdrücklichen Wunsch.
    await sorgeFuerEntschluesselte();

    const [{ entries }, { grants }] = await Promise.all([
      api.get<{ entries: LogRow[] }>('/api/access-log?limit=500'),
      api.get<{ grants: BerichtFreigabe[] }>('/api/grants'),
    ]);

    clear(status);
    status.append(el('p', { class: 'loading' }, 'PDF wird erzeugt …'));

    const { erzeugeZugriffsbericht } = await import('../bericht.ts');
    const blob = await erzeugeZugriffsbericht({
      kundenName: state.me!.displayName,
      dokumentAnzahl: state.documents.length,
      gesamtBytes: state.documents.reduce((summe, meta) => summe + meta.size_bytes, 0),
      ordnerAnzahl: state.ordner.length,
      freigaben: grants,
      eintraege: entries,
      dokumentNamen: new Map(state.opened.map((dokument) => [dokument.id, dokument.filename])),
    });

    const url = URL.createObjectURL(blob);
    const datum = new Date().toISOString().slice(0, 10);
    const verweis = el('a', {
      href: url,
      download: `zugriffsbericht-${datum}.pdf`,
    });
    document.body.append(verweis);
    verweis.click();
    verweis.remove();
    // Der Browser liest die Blob-URL beim Klick; danach darf sie weg. Kurze
    // Frist statt sofort, weil manche Browser den Download asynchron starten.
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);

    clear(status);
    status.append(
      hinweis(
        `Bericht erstellt (${entries.length} Protokolleinträge, ${grants.length} Freigaben). ` +
          'Die Datei liegt in Ihren Downloads.',
        'erfolg',
      ),
    );
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  } finally {
    if (knopf) knopf.disabled = false;
  }
}

// --- Kontodaten und Passwort ------------------------------------------------

function kontoKarte(): HTMLElement {
  return karte(
    el('h2', {}, 'Ihr Konto'),
    el(
      'table',
      { class: 'konto-tabelle' },
      el(
        'tbody',
        {},
        el('tr', {}, el('th', {}, 'Name'), el('td', {}, state.me!.displayName)),
        el('tr', {}, el('th', {}, 'E-Mail-Adresse'), el('td', {}, state.me!.email)),
        el('tr', {}, el('th', {}, 'Rolle'), el('td', {}, 'Kundin/Kunde')),
        el(
          'tr',
          {},
          el('th', {}, 'Verschlüsselung'),
          el('td', {}, state.me!.keySetupDone ? 'eingerichtet' : 'noch nicht eingerichtet'),
        ),
      ),
    ),
    el('h3', {}, 'Ihr Passwort'),
    el(
      'p',
      {},
      'Mit Ihrem Passwort melden Sie sich an UND schließen Ihre Dokumente auf. ' +
        'Es verlässt Ihr Gerät nie – auch wir kennen es nicht und können es ' +
        'nicht zurücksetzen.',
    ),
    passwortWechselAbschnitt(),
    el(
      'p',
      { class: 'leise' },
      'Passwort vergessen? Melden Sie sich ab und wählen Sie auf der ' +
        'Anmeldeseite „Passwort vergessen?“ – mit Ihrem Wiederherstellungs-Code ' +
        '(den zwölf Wörtern von der Einrichtung) legen Sie ein neues fest.',
    ),
    hinweis(
      'Bewahren Sie den Wiederherstellungs-Code sicher auf. Ohne Passwort und ' +
        'ohne Code kommt niemand mehr an Ihre Unterlagen – auch wir nicht.',
      'warnung',
    ),
  );
}

/**
 * Passwortwechsel im laufenden Betrieb (Etappe 3). Zusammengeklappt, damit
 * die Konto-Ansicht ruhig bleibt — wer wechseln will, klappt auf.
 */
function passwortWechselAbschnitt(): HTMLElement {
  const status = el('div', { id: 'pw-status' });

  return el(
    'details',
    { class: 'pw-wechsel' },
    el('summary', {}, 'Passwort ändern'),
    el(
      'p',
      { class: 'leise' },
      'Ihre Dokumente bleiben erhalten, und Ihr Wiederherstellungs-Code ' +
        'bleibt gültig. Andere angemeldete Geräte werden abgemeldet.',
    ),
    el('label', { for: 'pw-alt' }, 'Aktuelles Passwort'),
    passwortfeld({ id: 'pw-alt', autocomplete: 'current-password' }),
    el('label', { for: 'pw-neu1' }, 'Neues Passwort (mindestens 10 Zeichen)'),
    passwortfeld({ id: 'pw-neu1', autocomplete: 'new-password' }),
    el('label', { for: 'pw-neu2' }, 'Neues Passwort wiederholen'),
    passwortfeld({ id: 'pw-neu2', autocomplete: 'new-password' }),
    hinweis(
      'Nutzen Sie im Kundenbereich (Portal) dasselbe Passwort? Es ändert sich ' +
        'dort NICHT mit. Der Ein-Klick-Einstieg von dort in den Akten-Safe ' +
        'fragt dann wieder eigens nach dem Passwort – bis Sie das ' +
        'Kundenbereich-Passwort ebenfalls auf das neue umstellen.',
    ),
    status,
    el(
      'div',
      { class: 'knopfreihe' },
      el(
        'button',
        { id: 'pw-wechseln', onclick: () => void passwortAendern(status) },
        'Passwort jetzt ändern',
      ),
    ),
  );
}

async function passwortAendern(status: HTMLElement): Promise<void> {
  const alt = root.querySelector<HTMLInputElement>('#pw-alt')!.value;
  const neu1 = root.querySelector<HTMLInputElement>('#pw-neu1')!.value;
  const neu2 = root.querySelector<HTMLInputElement>('#pw-neu2')!.value;
  const knopf = root.querySelector<HTMLButtonElement>('#pw-wechseln')!;

  clear(status);
  if (!alt) {
    status.append(hinweis('Bitte geben Sie Ihr aktuelles Passwort ein.', 'warnung'));
    return;
  }
  if (neu1.length < 10) {
    status.append(hinweis('Bitte mindestens 10 Zeichen wählen.', 'warnung'));
    return;
  }
  if (neu1 !== neu2) {
    status.append(hinweis('Die beiden Eingaben stimmen nicht überein.', 'warnung'));
    return;
  }
  if (neu1 === alt) {
    status.append(hinweis('Das neue Passwort ist das alte.', 'warnung'));
    return;
  }

  knopf.disabled = true;
  knopf.textContent = 'Schlüssel werden neu verpackt …';
  try {
    await passwortWechseln({
      email: state.me!.email,
      currentPassword: alt,
      newPassword: neu1,
    });
    clear(status);
    for (const feld of ['#pw-alt', '#pw-neu1', '#pw-neu2']) {
      root.querySelector<HTMLInputElement>(feld)!.value = '';
    }
    status.append(
      hinweis(
        'Passwort geändert. Sie bleiben hier angemeldet; andere Geräte wurden ' +
          'abgemeldet. Ihr Wiederherstellungs-Code gilt unverändert.',
        'erfolg',
      ),
    );
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  } finally {
    knopf.disabled = false;
    knopf.textContent = 'Passwort jetzt ändern';
  }
}

// --- Dateneinsicht ----------------------------------------------------------

function dateneinsichtKarte(): HTMLElement {
  const gesamtGroesse = state.documents.reduce((summe, meta) => summe + meta.size_bytes, 0);
  const kategorien = new Set(
    state.documents.map((meta) => meta.category_label).filter((label) => label !== null),
  );

  const freigaben = el('li', {}, 'Freigaben: ', el('span', { class: 'loading' }, 'wird geladen …'));
  const vertrauenspersonen = el(
    'li',
    {},
    'Vertrauenspersonen: ',
    el('span', { class: 'loading' }, 'wird geladen …'),
  );

  void (async () => {
    try {
      const { grants } = await api.get<{ grants: Array<{ is_active: boolean }> }>('/api/grants');
      const aktiv = grants.filter((grant) => grant.is_active).length;
      freigaben.replaceChildren(
        `Freigaben: ${grants.length} insgesamt, davon ${aktiv} aktiv – Einzelheiten unter „Freigaben“`,
      );
    } catch {
      freigaben.replaceChildren('Freigaben: siehe Reiter „Freigaben“');
    }
  })();

  void (async () => {
    try {
      const eintraege = await ladeEigeneVertrauenspersonen();
      vertrauenspersonen.replaceChildren(
        `Vertrauenspersonen für den Notfall: ${eintraege.length} – Einzelheiten unter „Freigaben“`,
      );
    } catch {
      vertrauenspersonen.replaceChildren('Vertrauenspersonen: siehe Reiter „Freigaben“');
    }
  })();

  return karte(
    el('h2', {}, 'Dateneinsicht – was bei uns über Sie gespeichert ist'),
    el(
      'p',
      { class: 'leise' },
      'Diese Übersicht ist zugleich Ihre Auskunft nach Art. 15 DSGVO. Sie zeigt ' +
        'genau die Sicht, die unsere Server haben – gespeichert wird auf Servern ' +
        'in Deutschland.',
    ),
    el('h3', {}, 'Das liegt auf unseren Servern'),
    el(
      'ul',
      { class: 'einsicht-liste' },
      el('li', {}, `Ihr Konto: Anzeigename („${state.me!.displayName}“) und E-Mail-Adresse für die Anmeldung`),
      el(
        'li',
        {},
        `${state.documents.length} Dokument(e), zusammen ${formatBytes(gesamtGroesse)} – ` +
          'als verschlüsselte Dateien, deren Inhalt und Dateinamen wir nicht lesen können',
      ),
      el(
        'li',
        {},
        `Je Dokument: Größe, Ablagezeitpunkt und – falls die automatische Einsortierung ` +
          `genutzt wurde – die Kategorie (derzeit ${kategorien.size} Kategorie(n), z. B. „Rechnung“). ` +
          'Diese Angaben sind für uns lesbar.',
      ),
      el(
        'li',
        {},
        `${state.ordner.length} Ordner – die Ordnernamen sind für uns lesbar`,
      ),
      freigaben,
      vertrauenspersonen,
      el(
        'li',
        {},
        'Das Zugriffsprotokoll: wer wann auf Ihre Unterlagen zugegriffen hat ' +
          '(einsehbar unter „Freigaben“)',
      ),
    ),
    el('h3', {}, 'Das können wir nicht lesen'),
    el(
      'ul',
      { class: 'einsicht-liste' },
      el('li', {}, 'Den Inhalt Ihrer Dokumente – er wird auf Ihrem Gerät ver- und entschlüsselt'),
      el('li', {}, 'Die Dateinamen Ihrer Dokumente – sie sind ebenfalls verschlüsselt'),
      el('li', {}, 'Ihr Passwort und Ihren Wiederherstellungs-Code – beide verlassen Ihr Gerät nie'),
    ),
    el(
      'p',
      { class: 'leise' },
      'Nutzen Sie beim Ablegen „Automatisch einsortieren“, wird der Text des ' +
        'einen Dokuments einmalig zur Erkennung an unseren KI-Dienst gesendet ' +
        'und dort nicht gespeichert. Ohne das Häkchen passiert auch das nicht.',
    ),
    el(
      'p',
      {},
      'Ausführlich – Verschlüsselung, Speicherorte, Sicherung, Empfänger – ' +
        'als druckbare Seite: ',
      el(
        'a',
        { href: '/sicherheit.html', target: '_blank', rel: 'noopener' },
        'Sicherheit und Datenhaltung im Akten-Safe',
      ),
      '. Sie liefert die Angaben für eigene Prüfungen und Risikoanalysen.',
    ),
  );
}
